import { c as _c } from "react/compiler-runtime";
import type { BillingInterval, PaidPlan } from '@/types/billing-plan';
import { Loader2 } from 'lucide-react';
import type { Dispatch, SetStateAction } from 'react';
import { MONTHS_PER_YEAR, SUBSCRIPTION_PRICES } from '../constants';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { Label } from '@/components/ui/label';
import { NumericInput } from '@/components/ui/numeric-input';
import { RollingNumber } from '@/components/ui/rolling-number';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useInstallationMembersStatistics } from '@/data/installations/use-installation-members-statistics';
import { countActiveDevelopersForProSeatMinimum, countDevelopersWithRecentPrs, PRO_PLAN_ACTIVE_DEVELOPER_MIN_PRS, PRO_PLAN_ACTIVITY_WINDOW_DAYS } from '@/lib/billing/pro-seat-policy';
import { isProTierPlan, PAID_PLANS, PRO_PLAN_CONTACT_EMAIL } from '@/types/billing-plan';
type CheckoutSectionProps = {
  plan: PaidPlan;
  billingCycle: BillingInterval;
  installationId: number;
  quantity: number | null;
  setQuantity: Dispatch<SetStateAction<number | null>>;
  autoAssignSeats: boolean;
  isUpdatingAutoAssign: boolean;
  onAutoAssignToggle: () => void;
  onCheckout: () => void;
  isLoading: boolean;
  isInstallationLoading: boolean;
};
export function CheckoutSection(t0) {
  const $ = _c(80);
  const {
    plan,
    billingCycle,
    installationId,
    quantity,
    setQuantity,
    autoAssignSeats,
    isUpdatingAutoAssign,
    onAutoAssignToggle,
    onCheckout,
    isLoading,
    isInstallationLoading
  } = t0;
  const {
    data: membersStatisticsData,
    isPending: isMembersStatisticsLoading
  } = useInstallationMembersStatistics(installationId);
  const memberStatistics = membersStatisticsData?.memberStatistics;
  const distinctMemberPRCount = countDevelopersWithRecentPrs(memberStatistics);
  let t1;
  if ($[0] !== memberStatistics) {
    t1 = countActiveDevelopersForProSeatMinimum(memberStatistics);
    $[0] = memberStatistics;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const activeDeveloperCountForPro = t1;
  const {
    monthly: monthlyPrice,
    yearly: yearlyPrice
  } = SUBSCRIPTION_PRICES[plan];
  let t2;
  if ($[2] !== plan) {
    t2 = isProTierPlan(plan);
    $[2] = plan;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isProTier = t2;
  const safeQuantity = quantity ?? 0;
  const subtotalPrice = billingCycle === "yearly" ? monthlyPrice * safeQuantity * MONTHS_PER_YEAR : monthlyPrice * safeQuantity;
  const annualSavings = (monthlyPrice * MONTHS_PER_YEAR - yearlyPrice) * safeQuantity;
  const totalPrice = billingCycle === "yearly" ? yearlyPrice * safeQuantity : monthlyPrice * safeQuantity;
  const minimumSeatInput = isProTier ? Math.max(1, activeDeveloperCountForPro) : 1;
  const requiresMoreSeatsForPro = isProTier && safeQuantity < minimumSeatInput;
  const planName = PAID_PLANS[plan].name;
  const t3 = `Subscribe to ${planName}`;
  let t4;
  if ($[4] !== t3) {
    t4 = <h3 className="mb-6 text-2xl font-medium">{t3}</h3>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="flex items-center gap-1.5 text-base">Number of developer seats<InfoTooltip content="You can assign these developer seats to any GitHub users. cubic will only review PRs for developers who have a cubic seat assigned to them." /></p>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== distinctMemberPRCount || $[8] !== isMembersStatisticsLoading) {
    t6 = isMembersStatisticsLoading ? <Skeleton className="my-[3px] h-4 w-1/2" /> : distinctMemberPRCount > 0 && <p className="text-base text-zinc-400">{`${distinctMemberPRCount} ${distinctMemberPRCount === 1 ? "developer" : "developers"} opened PRs in the last 30 days`}</p>;
    $[7] = distinctMemberPRCount;
    $[8] = isMembersStatisticsLoading;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <div className="mb-4">{t5}{t6}</div>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== quantity || $[13] !== setQuantity) {
    t8 = <NumericInput value={quantity} setValue={setQuantity} min={1} className="mb-4" animateValue={true} />;
    $[12] = quantity;
    $[13] = setQuantity;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== isMembersStatisticsLoading || $[16] !== isProTier || $[17] !== minimumSeatInput) {
    t9 = isProTier && !isMembersStatisticsLoading && <p className="mb-2 text-sm text-zinc-400">{`Minimum: ${minimumSeatInput} ${minimumSeatInput === 1 ? "seat" : "seats"}`}</p>;
    $[15] = isMembersStatisticsLoading;
    $[16] = isProTier;
    $[17] = minimumSeatInput;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = isUpdatingAutoAssign || isInstallationLoading;
  let t11;
  if ($[19] !== autoAssignSeats || $[20] !== onAutoAssignToggle || $[21] !== t10) {
    t11 = <Checkbox id="auto-assign-seats-checkout" checked={autoAssignSeats} onCheckedChange={onAutoAssignToggle} variant="dark" disabled={t10} className="disabled:opacity-100" />;
    $[19] = autoAssignSeats;
    $[20] = onAutoAssignToggle;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Label htmlFor="auto-assign-seats-checkout" className="cursor-pointer text-base peer-disabled:opacity-100">Auto add seats when you add new developers in GitHub</Label>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11) {
    t13 = <div className="flex items-center space-x-2">{t11}{t12}</div>;
    $[24] = t11;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13 || $[27] !== t8 || $[28] !== t9) {
    t14 = <div className="mb-6">{t8}{t9}{t13}</div>;
    $[26] = t13;
    $[27] = t8;
    $[28] = t9;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = `$${monthlyPrice}`;
  let t16;
  if ($[30] !== t15) {
    t16 = <RollingNumber value={t15} />;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const t17 = String(safeQuantity);
  let t18;
  if ($[32] !== t17) {
    t18 = <RollingNumber value={t17} />;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const t19 = safeQuantity === 1 ? " seat" : " seats";
  const t20 = billingCycle === "yearly" ? " x 12 months" : "";
  let t21;
  if ($[34] !== t16 || $[35] !== t18 || $[36] !== t19 || $[37] !== t20) {
    t21 = <span className="text-zinc-400">{t16}{" x "}{t18}{t19}{t20}</span>;
    $[34] = t16;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const t22 = `$${subtotalPrice}`;
  let t23;
  if ($[39] !== t22) {
    t23 = <RollingNumber value={t22} />;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== t21 || $[42] !== t23) {
    t24 = <div className="flex items-center justify-between text-base">{t21}{t23}</div>;
    $[41] = t21;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== annualSavings || $[45] !== billingCycle) {
    t25 = billingCycle === "yearly" && <div className="mt-2 flex items-center justify-between text-base"><span className="text-zinc-400">Annual savings</span><span className="text-emerald-400"><RollingNumber value={`-$${annualSavings}`} /></span></div>;
    $[44] = annualSavings;
    $[45] = billingCycle;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <hr className="my-3 border-border" />;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  const t27 = billingCycle === "yearly" ? "Total per year" : "Total per month";
  let t28;
  if ($[48] !== t27) {
    t28 = <span className="text-zinc-400">{t27}</span>;
    $[48] = t27;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  const t29 = `$${totalPrice}`;
  let t30;
  if ($[50] !== t29) {
    t30 = <RollingNumber value={t29} />;
    $[50] = t29;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== t28 || $[53] !== t30) {
    t31 = <div className="flex items-center justify-between text-base font-medium">{t28}{t30}</div>;
    $[52] = t28;
    $[53] = t30;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <TooltipTrigger asChild={true}><span className="cursor-help text-base text-muted-foreground underline">Promo codes at checkout</span></TooltipTrigger>;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <div className="mt-2 text-right"><TooltipProvider><Tooltip>{t32}<TooltipContent side="top" className="max-w-md p-4"><p className="font-medium">You can apply promo codes in the next step in the Stripe checkout page. Note that we currently don't support promo codes on yearly plans.</p></TooltipContent></Tooltip></TooltipProvider></div>;
    $[56] = t33;
  } else {
    t33 = $[56];
  }
  let t34;
  if ($[57] !== t24 || $[58] !== t25 || $[59] !== t31) {
    t34 = <div className="mb-6 w-full rounded-lg border border-border bg-background p-5 lg:w-[65%]">{t24}{t25}{t26}{t31}{t33}</div>;
    $[57] = t24;
    $[58] = t25;
    $[59] = t31;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== isMembersStatisticsLoading || $[62] !== isProTier || $[63] !== minimumSeatInput || $[64] !== planName || $[65] !== requiresMoreSeatsForPro) {
    t35 = isProTier && requiresMoreSeatsForPro && !isMembersStatisticsLoading && <Alert className="mb-4 border-amber-500/30 bg-amber-500/10 text-amber-100"><div><AlertTitle className="text-amber-100">{`You must select at least ${minimumSeatInput} ${minimumSeatInput === 1 ? "seat" : "seats"} for the ${planName} plan.`}</AlertTitle><AlertDescription className="text-amber-100/90"><p>{`${planName} requires a seat for every active developer (${PRO_PLAN_ACTIVE_DEVELOPER_MIN_PRS}+ PRs in the last ${PRO_PLAN_ACTIVITY_WINDOW_DAYS} days) because codebase scans run across the entire codebase.`}</p><p className="mt-2">Need an exception? Contact<a className="ml-1 underline" href={`mailto:${PRO_PLAN_CONTACT_EMAIL}`}>{PRO_PLAN_CONTACT_EMAIL}</a>.</p></AlertDescription></div></Alert>;
    $[61] = isMembersStatisticsLoading;
    $[62] = isProTier;
    $[63] = minimumSeatInput;
    $[64] = planName;
    $[65] = requiresMoreSeatsForPro;
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  const t36 = isLoading || safeQuantity < minimumSeatInput || isInstallationLoading || isProTier && isMembersStatisticsLoading;
  let t37;
  if ($[67] !== isLoading) {
    t37 = isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />;
    $[67] = isLoading;
    $[68] = t37;
  } else {
    t37 = $[68];
  }
  let t38;
  if ($[69] !== onCheckout || $[70] !== t36 || $[71] !== t37) {
    t38 = <Button variant="default" size="lg" className="w-full" disabled={t36} onClick={onCheckout}>{t37}Continue to checkout</Button>;
    $[69] = onCheckout;
    $[70] = t36;
    $[71] = t37;
    $[72] = t38;
  } else {
    t38 = $[72];
  }
  let t39;
  if ($[73] !== t14 || $[74] !== t34 || $[75] !== t35 || $[76] !== t38 || $[77] !== t4 || $[78] !== t7) {
    t39 = <div className="mx-auto mt-8 w-full max-w-3xl rounded-xl border border-border bg-[#101011] p-4 sm:p-8">{t4}{t7}{t14}{t34}{t35}{t38}</div>;
    $[73] = t14;
    $[74] = t34;
    $[75] = t35;
    $[76] = t38;
    $[77] = t4;
    $[78] = t7;
    $[79] = t39;
  } else {
    t39 = $[79];
  }
  return t39;
}